import { Meteor } from 'meteor/meteor';
import { Mongo } from 'meteor/mongo';
import { check, Match } from 'meteor/check';
import { addError } from '/imports/utils';

export const Errors = {
  required: 'required',
  mustBeString: 'mustBeString',
  mustBeNumber: 'mustBeNumber',
  mustBeArray: 'mustBeArray',
  doesNotExist: 'doesNotExist',
  mustBeBeforeEndTime: 'mustBeBeforeEndTime',
  mustBeAfterStartTime: 'mustBeAfterStartTime',
  errorCreatingUser: 'errorCreatingUser',
  staffAlreadyExists: 'staffAlreadyExists',
  validationError: 'validationError',
  mustBeUnique: 'mustBeUnique',
  failedRegex: 'failedRegex',
  lessThanMin: 'lessThanMin',
  tooLong: 'tooLong',
  invalid: 'invalid',
};

/**
 * Adds an error if no document matches the query (skipped on client).
 * @param errors - Accumulating field-error map.
 * @param key - Field key the error is recorded under.
 * @param collection - Collection to query.
 * @param query - Id or Mongo query identifying the expected document.
 * @returns True (always; error pushed into `errors` when missing).
 */
export const validateExists = async <T>(
  errors: { [key: string]: string[] },
  key: string,
  collection: Mongo.Collection<any>,
  query: string | Mongo.Query<T>
) => {
  if (Meteor.isClient) return true;
  if (!await collection.findOneAsync(query)) return addError(errors, key as string, Errors.doesNotExist);
  return true;
};

/**
 * Validates that a field is an array (or absent when not required).
 * @param errors - Accumulating field-error map.
 * @param obj - Object being validated.
 * @param key - Field key to check.
 * @param options - Set `required` to disallow undefined.
 * @returns True (errors pushed into `errors` on failure).
 */
export const validateArray = <T>(
  errors: { [key: string]: string[] },
  obj: T,
  key: keyof T,
  options?: {
    required?: boolean
  }
) => {
  const value = obj[key];
  if (!options?.required && (value === undefined)) return true;
  if (options?.required && value === undefined) return addError(errors, key as string, Errors.mustBeArray);
  if (!Array.isArray(value)) return addError(errors, key as string, Errors.mustBeArray);
  return true;
};

/**
 * Validates that a field is a non-empty string (when required).
 * @param errors - Accumulating field-error map.
 * @param obj - Object being validated.
 * @param key - Field key to check.
 * @param options - Set `required` to disallow undefined/blank.
 * @returns True (errors pushed into `errors` on failure).
 */
export const validateString = <T>(
  errors: { [key: string]: string[] },
  obj: T,
  key: keyof T,
  options?: {
    required?: boolean,
  }
) => {
  const value = obj[key];
  if (!options?.required && (value === undefined)) return true;
  if (options?.required && value === undefined) return addError(errors, key as string, Errors.required);
  if (typeof value !== 'string') return addError(errors, key as string, Errors.mustBeString);
  if (options?.required && value.trim().length === 0) return addError(errors, key as string, Errors.required);
  return true;
};

/**
 * Validates that a field is a number, optionally above a minimum.
 * @param errors - Accumulating field-error map.
 * @param obj - Object being validated.
 * @param key - Field key to check.
 * @param options - Set `required` and/or `min`.
 * @returns True (errors pushed into `errors` on failure).
 */
export const validateNumber = <T>(
  errors: { [key: string]: string[] },
  obj: T,
  key: keyof T,
  options?: {
    required?: boolean,
    min?: number,
  }
) => {
  const value = obj[key];
  if (!options?.required && (value === undefined)) return true;
  if (options?.required && value === undefined) return addError(errors, key as string, Errors.required);
  if (typeof value !== 'number') return addError(errors, key as string, Errors.mustBeNumber);
  if (typeof options?.min === 'number' && value < options.min) return addError(errors, key as string, Errors.lessThanMin);
  return true;
};

/**
 * Validates that a field is a Date instance.
 * @param errors - Accumulating field-error map.
 * @param obj - Object being validated.
 * @param key - Field key to check.
 * @param options - Set `required` to disallow undefined.
 * @returns True (errors pushed into `errors` on failure).
 */
export const validateDate = <T>(
  errors: { [key: string]: string[] },
  obj: T,
  key: keyof T,
  options?: {
    required?: boolean
  }
) => {
  const value = obj[key];
  if (!options?.required && (value === undefined)) return true;
  if (options?.required && value === undefined) return addError(errors, key as string, Errors.required);
  if (!(value instanceof Date)) return addError(errors, key as string, Errors.invalid);
  return true;
};

/**
 * Validates that a field value is unique in the collection, ignoring the doc's own _id.
 * @param errors - Accumulating field-error map.
 * @param obj - Object being validated (may carry _id).
 * @param key - Field key to check for uniqueness.
 * @param collection - Collection to search.
 * @returns True (errors pushed into `errors` on conflict).
 */
export const validateUnique = async <T>(
  errors: { [key: string]: string[] },
  obj: any,
  key: keyof T,
  collection: Mongo.Collection<any>
) => {
  const value = obj[key];
  if (value === undefined) return true;
  if (obj._id) {
    const query = { [key]: value, _id: { $ne: obj._id } };
    return await collection.findOneAsync(query) ? addError(errors, key as string, Errors.mustBeUnique) : true;
  }
  else {
    const query = { [key]: value };
    return await collection.findOneAsync(query) ? addError(errors, key as string, Errors.mustBeUnique) : true;
  }
};

/**
 * Validates a string field against a regex (no-op for non-strings).
 * @param errors - Accumulating field-error map.
 * @param obj - Object being validated.
 * @param key - Field key to check.
 * @param re - Pattern the value must match.
 * @returns True (errors pushed into `errors` on mismatch).
 */
export const validateRegex = <T>(
  errors: { [key: string]: string[] },
  obj: T,
  key: keyof T,
  re: RegExp
) => {
  const value = obj[key];
  if (typeof value === 'string') {
    if (!re.test(value)) return addError(errors, key as string, Errors.failedRegex);
  }
  return true;
};

/**
 * Validates that a boolean field is explicitly true.
 * @param errors - Accumulating field-error map.
 * @param obj - Object being validated.
 * @param key - Field key to check.
 * @returns True (errors pushed into `errors` when not true).
 */
export const validateTrue = <T>(
  errors: { [key: string]: string[] },
  obj: T,
  key: keyof T,
) => {
  const value = obj[key];
  return typeof value === 'boolean' && !!value ? true : addError(errors, key as string, Errors.required);
};

/**
 * Validates that a field is a boolean or undefined.
 * @param errors - Accumulating field-error map.
 * @param obj - Object being validated.
 * @param key - Field key to check.
 * @returns True (errors pushed into `errors` on failure).
 */
export const validateBoolean = <T>(
  errors: { [key: string]: string[] },
  obj: T,
  key: keyof T,
) => {
  const value = obj[key];
  return value === undefined || value === true || value === false ? true : addError(errors, key as string, Errors.required);
};

/**
 * Validates that a field matches the expected address shape.
 * @param errors - Accumulating field-error map.
 * @param obj - Object being validated.
 * @param key - Field key holding the address.
 * @param options - Set `required` to disallow undefined.
 * @returns True (errors pushed into `errors` on failure).
 */
export const validateAddress = <T>(
  errors: { [key: string]: string[] },
  obj: T,
  key: keyof T,
  options?: {
    required?: boolean,
  }
) => {
  const value = obj[key];
  if (!options?.required && (value === undefined)) return true;
  if (options?.required && value === undefined) return addError(errors, key as string, Errors.required);
  try {
    check(value, {
      number: Match.Maybe(String),
      route: Match.Maybe(String),
      city: String,
      state: String,
      stateName: String,
      country: String,
      countryName: String,
      zipcode: Match.Maybe(String),
      line: String,
      line2: Match.Maybe(String),
      formatted: String,
      location: { lat: Number, lng: Number },
    });
  }
  catch (error) {
    console.log('error', error);
    addError(errors, key as string, Errors.invalid);
  }
  return true;
};